import { Ellipsis, Pencil, SquarePen, Trash2 } from 'lucide-react-native';

import type { HeaderProps } from '@/components/header';
import type {
  HeaderButtonItem,
  HeaderMenuAction,
  HeaderMenuItem,
} from '@/components/headerItems/types';

type SaveHeaderItemOptions = {
  onPress: () => void;
  isDisabled?: boolean;
  isSaving?: boolean;
};

/** Edit and Save take each other's place in the trailing slot, so they morph as one item. */
const EDIT_SAVE_MORPH_KEY = 'edit-save';

/**
 * The header actions every screen shares, as items for `Header`'s `leftItems` and
 * `rightItems`. Taking them from here keeps the wording, the role and the icon of an action
 * the same wherever it appears, which is what lets each platform style it consistently.
 *
 * Cancel, Close and Save are words on every platform, not glyphs: on a form a reader has put
 * work into, leaving and keeping must not be mistaken for each other. Cancel and Close go in
 * the leading slot, where both iOS sheets and Material full-screen dialogs put the way out.
 */
export const headerItem = {
  cancel: (onPress: () => void, isHidden = false): HeaderButtonItem => ({
    type: 'button',
    key: 'cancel',
    label: 'Cancel',
    onPress,
    isHidden,
  }),

  save: (
    { onPress, isDisabled = false, isSaving = false }: SaveHeaderItemOptions,
    isHidden = false
  ): HeaderButtonItem => ({
    type: 'button',
    key: 'save',
    morphKey: EDIT_SAVE_MORPH_KEY,
    label: 'Save',
    role: 'confirm',
    onPress,
    isDisabled,
    isLoading: isSaving,
    isHidden,
  }),

  close: (onPress: () => void, isHidden = false): HeaderButtonItem => ({
    type: 'button',
    key: 'close',
    label: 'Close',
    onPress,
    isHidden,
  }),

  /**
   * Starting an inquiry with the person on screen. It wears the Inquiry tab's own glyph, so
   * the action reads the same here as it does in the tab bar. `label` names who it is to, for
   * the spoken label: "Start an inquiry with Jane Doe".
   */
  startInquiry: (label: string, onPress: () => void, isHidden = false): HeaderButtonItem => ({
    type: 'button',
    key: 'start-inquiry',
    label,
    icon: { sf: 'square.and.pencil', lucide: SquarePen },
    onPress,
    isHidden,
  }),

  /**
   * An inquiry's own actions, such as closing it, behind a "…" in a phone's bar, where a word
   * in the trailing slot leaves too little room beside the back button. One key wherever it
   * appears, so a screen that shows it hidden while loading and visible once loaded keeps the
   * same slot. A screen still loading shows it disabled, so the "…" is in the bar as the
   * screen is pushed and morphs in with it rather than appearing once the screen has loaded.
   */
  inquiryActions: (
    actions: HeaderMenuAction[],
    isHidden = false,
    isDisabled = false
  ): HeaderMenuItem => ({
    type: 'menu',
    key: 'inquiry-actions',
    label: 'Inquiry actions',
    icon: { sf: 'ellipsis', lucide: Ellipsis },
    actions,
    isDisabled,
    isHidden,
  }),

  /** Deleting the draft inquiry being composed. */
  deleteDraft: (onPress: () => void, isDisabled = false, isHidden = false): HeaderButtonItem => ({
    type: 'button',
    key: 'delete-draft',
    label: 'Delete draft',
    icon: { sf: 'trash', lucide: Trash2 },
    role: 'destructive',
    onPress,
    isDisabled,
    isHidden,
  }),

  /** `label` names what is being edited, for the spoken label: "Edit contact details". */
  edit: (label: string, onPress: () => void, isHidden = false): HeaderButtonItem => ({
    type: 'button',
    key: 'edit',
    morphKey: EDIT_SAVE_MORPH_KEY,
    label,
    icon: { sf: 'pencil', lucide: Pencil },
    onPress,
    isHidden,
  }),
};

/**
 * The `Header` props that give an edit screen a Cancel button in place of a back arrow.
 *
 * Cancel is the screen's own action on every platform, in the leading slot. It must not be the
 * header's Back relabelled: that skips `onCancel`, so unsaved edits were dropped without the
 * discard prompt, and after a fresh load it went to the Directory instead of the screen's parent.
 */
export function getCancelHeaderProps(onCancel: () => void): Partial<HeaderProps> {
  return { leftItems: [headerItem.cancel(onCancel)] };
}
